// Closed-grammar RRULE emitter + parser for the inspection-schedule recurrence
// builder. The recurrence engine (apps/api/internal/compliance/recurrence/)
// VALIDATES a superset of what it HONORS in generation — BYMONTH, ordinal BYDAY
// prefixes, and several other keys validate but silently no-op. The builder
// therefore emits ONLY the honored grammar:
//
//   FREQ={DAILY|WEEKLY|MONTHLY|YEARLY}
//     [;INTERVAL=n]                        n >= 2 (omitted when 1)
//     [;COUNT=1..366]                      only when Ends = After N
//     [;BYDAY=<plain 2-letter codes>]      only when FREQ=WEEKLY
//     [;BYMONTHDAY=<list of -1, 1..31>]    only when FREQ=MONTHLY
//
// Never emitted: UNTIL ("Ends on" maps to the schedule's end_date column —
// UNTIL carries the engine's start+5y cap and re-validation surprises on
// start_date changes), BYMONTH, ordinal BYDAY, the "RRULE:" prefix, whitespace,
// duplicate keys. MONTHLY always carries an explicit BYMONTHDAY because a bare
// FREQ=MONTHLY advances with Go AddDate, which normalizes month-end (Jan 31 +
// 1 month = Mar 3) and silently drifts the day-of-month.

export const WEEKDAY_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

export type Weekday = (typeof WEEKDAY_ORDER)[number];

export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "yearly";

export type RecurrenceEnds =
  | { type: "never" }
  | { type: "on"; date: string }
  | { type: "after"; count: number };

/**
 * Canonical builder state. Invariant: `weekdays` is populated only when
 * `freq === "weekly"` and `monthDays` only when `freq === "monthly"` (the
 * other axis stays empty — frequency switches reset the chip axes via
 * `defaultStateFor`). `-1` in `monthDays` means "Last day of month".
 * `ends.on` mirrors the wizard-level `end_date`; it never reaches the rule.
 */
export interface RecurrenceBuilderState {
  freq: RecurrenceFrequency;
  interval: number;
  weekdays: ReadonlySet<Weekday>;
  monthDays: ReadonlySet<number>;
  ends: RecurrenceEnds;
}

export type RecurrencePreset =
  | "daily"
  | "weekly"
  | "everyWeekday"
  | "monthly"
  | "quarterly"
  | "yearly";

export type ParseRRuleResult =
  | { kind: "ok"; state: RecurrenceBuilderState; endDateFromUntil: string | null }
  | { kind: "unsupported"; reason: string };

const FREQ_TOKEN: Record<RecurrenceFrequency, string> = {
  daily: "DAILY",
  weekly: "WEEKLY",
  monthly: "MONTHLY",
  yearly: "YEARLY",
};

const TOKEN_TO_FREQ: Readonly<Record<string, RecurrenceFrequency>> = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  YEARLY: "yearly",
};

// getDay() index (0 = Sunday) to chip code.
const GETDAY_TO_CODE = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

/**
 * Parse a `YYYY-MM-DD` string into a local-time Date. `new Date("YYYY-MM-DD")`
 * parses as UTC midnight, which shifts the calendar day (and weekday) in
 * timezones west of UTC — component construction stays in local time.
 */
export function parseLocalDate(isoDate: string): Date {
  const [year = 0, month = 1, day = 1] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Weekday chip code of a `YYYY-MM-DD` date. */
export function weekdayOf(isoDate: string): Weekday {
  // getDay() is always 0-6; the fallback only satisfies the indexed-access type.
  return GETDAY_TO_CODE[parseLocalDate(isoDate).getDay()] ?? "MO";
}

/** Day-of-month (1-31) of a `YYYY-MM-DD` date. */
export function dayOfMonthOf(isoDate: string): number {
  return parseLocalDate(isoDate).getDate();
}

/**
 * Serialize builder state into the engine's honored grammar. Deterministic:
 * key order FREQ → INTERVAL → COUNT → BYDAY → BYMONTHDAY; INTERVAL omitted
 * when 1; BYDAY sorted MO→SU; BYMONTHDAY ascending with -1 last; `;`-joined
 * with no whitespace and no prefix. `Ends = on` emits nothing — the date
 * travels as the schedule's `end_date` column instead of UNTIL.
 */
export function emitRRule(state: RecurrenceBuilderState): string {
  const parts = [`FREQ=${FREQ_TOKEN[state.freq]}`];
  if (state.interval > 1) {
    parts.push(`INTERVAL=${state.interval}`);
  }
  if (state.ends.type === "after") {
    parts.push(`COUNT=${state.ends.count}`);
  }
  if (state.freq === "weekly" && state.weekdays.size > 0) {
    const days = WEEKDAY_ORDER.filter((day) => state.weekdays.has(day));
    parts.push(`BYDAY=${days.join(",")}`);
  }
  if (state.freq === "monthly" && state.monthDays.size > 0) {
    const positives = [...state.monthDays].filter((day) => day > 0).sort((a, b) => a - b);
    const days = state.monthDays.has(-1) ? [...positives, -1] : positives;
    parts.push(`BYMONTHDAY=${days.join(",")}`);
  }
  return parts.join(";");
}

/**
 * Default state for a frequency, anchored to the start date: weekly
 * pre-selects the start date's weekday chip, monthly pre-selects its
 * day-of-month. Used for the initial builder state and for the
 * frequency-switch reset (the chip axes are frequency-scoped; the caller
 * preserves interval and ends across switches).
 */
export function defaultStateFor(
  freq: RecurrenceFrequency,
  startDate: string,
): RecurrenceBuilderState {
  return {
    freq,
    interval: 1,
    weekdays: freq === "weekly" ? new Set([weekdayOf(startDate)]) : new Set(),
    monthDays: freq === "monthly" ? new Set([dayOfMonthOf(startDate)]) : new Set(),
    ends: { type: "never" },
  };
}

/** Builder state for each "Repeats" preset, anchored to the start date. */
export function presetState(preset: RecurrencePreset, startDate: string): RecurrenceBuilderState {
  switch (preset) {
    case "daily":
      return defaultStateFor("daily", startDate);
    case "weekly":
      return defaultStateFor("weekly", startDate);
    case "everyWeekday":
      return {
        ...defaultStateFor("weekly", startDate),
        weekdays: new Set<Weekday>(["MO", "TU", "WE", "TH", "FR"]),
      };
    case "monthly":
      return defaultStateFor("monthly", startDate);
    case "quarterly":
      return { ...defaultStateFor("monthly", startDate), interval: 3 };
    case "yearly":
      return defaultStateFor("yearly", startDate);
  }
}

function unsupported(reason: string): ParseRRuleResult {
  return { kind: "unsupported", reason };
}

function parseUnsignedInt(value: string): number | null {
  return /^\d+$/.test(value) ? Number(value) : null;
}

/**
 * Convert an engine-accepted UNTIL value (`YYYYMMDD` or `YYYYMMDDTHHMMSSZ`)
 * into a `YYYY-MM-DD` date string, or null when the shape is unrecognized.
 */
function untilToIsoDate(value: string): string | null {
  const match = /^(\d{4})(\d{2})(\d{2})(?:T\d{6}Z)?$/.exec(value);
  if (!match) {
    return null;
  }
  const [, year = "", month = "", day = ""] = match;
  const monthNum = Number(month);
  const dayNum = Number(day);
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    return null;
  }
  return `${year}-${month}-${day}`;
}

function isWeekday(value: string): value is Weekday {
  // Widening the literal tuple: its own `includes` signature accepts only
  // Weekday and would reject the arbitrary string being tested.
  return (WEEKDAY_ORDER as readonly string[]).includes(value);
}

/**
 * Parse a stored rule back into builder state. `ok` iff every part round-trips
 * into a faithful builder state; everything else is `unsupported` and the edit
 * surface renders the read-only unsupported-rule mode instead of half-rendering
 * controls that misrepresent what the rule generates. The `reason` string is a
 * diagnostic for developers/support, not display copy — the UI shows its own
 * translated warning.
 *
 * Notable verdicts:
 * - `UNTIL` (either engine format) rehydrates as `ends: on` with the date also
 *   surfaced in `endDateFromUntil` — the caller saves it as `end_date`, a
 *   one-way normalization away from UNTIL's footguns.
 * - Bare `FREQ=MONTHLY` (no BYMONTHDAY) is unsupported: its generation drifts
 *   at month-end via AddDate normalization, so no faithful chip state exists.
 * - Bare `FREQ=WEEKLY` (no BYDAY) is unsupported: the weekday-chip floor is one
 *   selected chip, and without the start date the parser cannot derive which
 *   chip the anchored generation would hit.
 * - `BYMONTHDAY` negatives other than -1 are engine-legal but have no builder
 *   control, so they are unsupported.
 */
export function parseRRule(rrule: string): ParseRRuleResult {
  if (rrule === "") {
    return unsupported("empty rule");
  }
  const seen = new Set<string>();
  let freq: RecurrenceFrequency | null = null;
  let interval = 1;
  let count: number | null = null;
  let until: string | null = null;
  let weekdays: Set<Weekday> | null = null;
  let monthDays: Set<number> | null = null;

  for (const part of rrule.split(";")) {
    const eq = part.indexOf("=");
    if (eq <= 0 || eq === part.length - 1) {
      return unsupported(`malformed part "${part}"`);
    }
    const key = part.slice(0, eq);
    const value = part.slice(eq + 1);
    if (seen.has(key)) {
      return unsupported(`duplicate key "${key}"`);
    }
    seen.add(key);
    switch (key) {
      case "FREQ": {
        const parsed = TOKEN_TO_FREQ[value];
        if (!parsed) {
          return unsupported(`FREQ "${value}" is outside the builder grammar`);
        }
        freq = parsed;
        break;
      }
      case "INTERVAL": {
        const parsed = parseUnsignedInt(value);
        if (parsed === null || parsed < 1) {
          return unsupported(`INTERVAL "${value}" is not a positive integer`);
        }
        interval = parsed;
        break;
      }
      case "COUNT": {
        const parsed = parseUnsignedInt(value);
        if (parsed === null || parsed < 1 || parsed > 366) {
          return unsupported(`COUNT "${value}" is outside 1..366`);
        }
        count = parsed;
        break;
      }
      case "UNTIL": {
        const parsed = untilToIsoDate(value);
        if (parsed === null) {
          return unsupported(`UNTIL "${value}" is not a recognized date form`);
        }
        until = parsed;
        break;
      }
      case "BYDAY": {
        const days = new Set<Weekday>();
        for (const code of value.split(",")) {
          if (!isWeekday(code)) {
            return unsupported(`BYDAY value "${code}" is not a plain two-letter weekday code`);
          }
          if (days.has(code)) {
            return unsupported(`BYDAY repeats "${code}"`);
          }
          days.add(code);
        }
        weekdays = days;
        break;
      }
      case "BYMONTHDAY": {
        const days = new Set<number>();
        for (const token of value.split(",")) {
          const parsed = /^-?\d+$/.test(token) ? Number(token) : null;
          if (parsed === null || (parsed !== -1 && (parsed < 1 || parsed > 31))) {
            return unsupported(`BYMONTHDAY value "${token}" has no builder control`);
          }
          if (days.has(parsed)) {
            return unsupported(`BYMONTHDAY repeats "${token}"`);
          }
          days.add(parsed);
        }
        monthDays = days;
        break;
      }
      default:
        return unsupported(`key "${key}" is outside the builder grammar`);
    }
  }

  if (freq === null) {
    return unsupported("missing FREQ");
  }
  if (count !== null && until !== null) {
    return unsupported("COUNT and UNTIL are mutually exclusive");
  }
  if (weekdays !== null && freq !== "weekly") {
    return unsupported("BYDAY is only honored for weekly rules");
  }
  if (monthDays !== null && freq !== "monthly") {
    return unsupported("BYMONTHDAY is only honored for monthly rules");
  }
  if (freq === "weekly" && weekdays === null) {
    return unsupported("weekly rule without BYDAY cannot seed the weekday chips");
  }
  if (freq === "monthly" && monthDays === null) {
    return unsupported("monthly rule without BYMONTHDAY drifts at month end");
  }

  const ends: RecurrenceEnds =
    count !== null
      ? { type: "after", count }
      : until !== null
        ? { type: "on", date: until }
        : { type: "never" };

  return {
    kind: "ok",
    state: {
      freq,
      interval,
      weekdays: weekdays ?? new Set(),
      monthDays: monthDays ?? new Set(),
      ends,
    },
    endDateFromUntil: until,
  };
}
